import { GraduationCap, Video, BookOpen, Wifi, CheckCircle2, Clock, XCircle } from 'lucide-react';
import { useApprenantPedagogie } from '../hooks/useApprenantPedagogie.js';
import { CollapsibleSlice } from '../../../components/CollapsibleSlice.js';

interface Props {
  apprenantId: string;
}

/**
 * Slice PedagogieCard — domaine Production pédagogique
 *
 * Affiche heures réalisées (sync + async), connexion Moodle, progression
 * cours, dernières séances, devoirs.
 *
 * Collapsible : KPIs toujours visible, séances/devoirs dépliables.
 */
export function PedagogieCard({ apprenantId }: Props) {
  const { data: p, isLoading, error } = useApprenantPedagogie(apprenantId);

  if (isLoading || error || !p) {
    return (
      <CollapsibleSlice
        title="Pédagogie réalisée"
        icon={<GraduationCap className="text-primary-500 h-4 w-4" />}
      >
        <div className="text-sm text-neutral-500">{error?.message ?? 'Chargement...'}</div>
      </CollapsibleSlice>
    );
  }

  const syncPct = Math.round((p.heuresSyncDone / p.heuresSyncTotal) * 100);
  const asyncPct = Math.round((p.heuresAsyncDone / p.heuresAsyncTotal) * 100);
  const totalDone = p.heuresSyncDone + p.heuresAsyncDone;
  const totalTotal = p.heuresSyncTotal + p.heuresAsyncTotal;
  const totalPct = Math.round((totalDone / totalTotal) * 100);

  return (
    <CollapsibleSlice
      title="Pédagogie réalisée"
      icon={<GraduationCap className="text-primary-500 h-4 w-4" />}
      summary={
        <div className="space-y-2.5">
          {/* Progression globale */}
          <div>
            <div className="mb-1 flex items-baseline justify-between text-xs">
              <span className="font-semibold text-neutral-700">Progression globale</span>
              <span className="text-neutral-500">
                <span className="text-primary-700 text-base font-bold">{totalDone}h</span>
                <span className="text-neutral-400"> / {totalTotal}h</span>
                <span className="text-primary-700 ml-1 font-semibold">({totalPct}%)</span>
              </span>
            </div>
            <div className="h-2 w-full overflow-hidden rounded-full bg-neutral-100">
              <div
                className="h-full rounded-full bg-emerald-500 transition-all"
                style={{ width: `${totalPct}%` }}
              />
            </div>
          </div>
          {/* Détail sync/async + Moodle connection */}
          <div className="grid grid-cols-3 gap-2">
            <MiniKpi
              icon={<Video className="h-3 w-3" />}
              label="Visio"
              value={`${p.heuresSyncDone}/${p.heuresSyncTotal}h`}
              pct={syncPct}
            />
            <MiniKpi
              icon={<BookOpen className="h-3 w-3" />}
              label="E-learning"
              value={`${p.heuresAsyncDone}/${p.heuresAsyncTotal}h`}
              pct={asyncPct}
            />
            <MiniKpi
              icon={<Wifi className="h-3 w-3" />}
              label="Connexion Moodle"
              value={`${p.moodleConnectionHours.toFixed(1)}h`}
              extra={`${p.moodleProgressPct}% cours`}
            />
          </div>
          {p.derniereConnexion && (
            <div className="text-[10px] text-neutral-500">
              Dernière connexion :{' '}
              <span className="font-medium text-neutral-700">
                {new Date(p.derniereConnexion).toLocaleString('fr-FR', {
                  dateStyle: 'short',
                  timeStyle: 'short',
                })}
              </span>
            </div>
          )}
        </div>
      }
    >
      {/* Séances */}
      <div className="mb-3">
        <div className="mb-1.5 text-[10px] font-semibold uppercase tracking-wider text-neutral-500">
          Dernières séances ({p.seances.length})
        </div>
        <ul className="space-y-1">
          {p.seances.map((s, i) => (
            <li
              key={i}
              className="flex items-center gap-2 rounded px-2 py-1 text-xs hover:bg-neutral-50"
            >
              {s.emargementSigne ? (
                <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />
              ) : (
                <XCircle className="h-3.5 w-3.5 text-rose-500" />
              )}
              <span className="w-20 text-[10px] text-neutral-500">
                {new Date(s.date).toLocaleDateString('fr-FR')}
              </span>
              <span className="flex-1 truncate">{s.topic}</span>
              <span className="text-[10px] text-neutral-400">
                {s.type} · {s.duration}min
              </span>
            </li>
          ))}
        </ul>
      </div>

      {/* Devoirs */}
      <div>
        <div className="mb-1.5 text-[10px] font-semibold uppercase tracking-wider text-neutral-500">
          Devoirs ({p.devoirs.filter((d) => d.submittedAt).length}/{p.devoirs.length} rendus)
        </div>
        <ul className="space-y-1">
          {p.devoirs.map((d, i) => (
            <li
              key={i}
              className="flex items-center gap-2 rounded px-2 py-1 text-xs hover:bg-neutral-50"
            >
              {d.submittedAt ? (
                <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />
              ) : (
                <Clock className="h-3.5 w-3.5 text-amber-500" />
              )}
              <span className="flex-1 truncate">{d.title}</span>
              <span className="text-[10px] text-neutral-400">
                Pour le {new Date(d.dueDate).toLocaleDateString('fr-FR')}
              </span>
              {d.score !== null && (
                <span className="rounded bg-emerald-50 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-700">
                  {d.score}/20
                </span>
              )}
            </li>
          ))}
        </ul>
      </div>
    </CollapsibleSlice>
  );
}

function MiniKpi({
  icon,
  label,
  value,
  pct,
  extra,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  pct?: number;
  extra?: string;
}) {
  return (
    <div className="rounded-md border border-neutral-200 bg-neutral-50 p-2">
      <div className="flex items-center gap-1 text-[9px] uppercase tracking-wider text-neutral-500">
        {icon}
        {label}
      </div>
      <div className="mt-0.5 text-sm font-bold text-neutral-900">{value}</div>
      {pct !== undefined && (
        <div className="mt-1 h-1 w-full overflow-hidden rounded-full bg-white">
          <div className="bg-primary-500 h-full rounded-full" style={{ width: `${pct}%` }} />
        </div>
      )}
      {extra && <div className="mt-0.5 text-[9px] text-neutral-500">{extra}</div>}
    </div>
  );
}
